import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, DollarSign, Send, CheckCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import BeautyServicesSection from "./BeautyServicesSection";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.includes("/api/files/") && !url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    const filePath = url.substring(url.indexOf("/api/files/"));
    return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const serviceCategories = [
  { value: "braids", label: "Braids" },
  { value: "twists", label: "Twists" },
  { value: "crochet", label: "Crochet & Ponytails" },
  { value: "locs", label: "Locs" },
  { value: "mens", label: "Men's" },
  { value: "other", label: "Other" },
];

const ServicesSection = () => {
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState("braids");
  const [priceFilter, setPriceFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
        if (res.data.length) trackEvent("view_item_list", { item_list_name: "Braiding Services", items_count: res.data.length });
      } catch (err) {
        console.error("Failed to load services:", err);
      }
    };
    fetchServices();
  }, []);

  const scrollToBooking = () => {
    const el = document.querySelector("#booking");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const parseTime = (dur) => {
    if (!dur) return 0;
    const match = dur.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const applyFilters = (list) => {
    let result = list;
    if (priceFilter === "under100") result = result.filter(s => s.starting_price < 100);
    else if (priceFilter === "100to200") result = result.filter(s => s.starting_price >= 100 && s.starting_price <= 200);
    else if (priceFilter === "over200") result = result.filter(s => s.starting_price > 200);

    if (timeFilter === "under1") result = result.filter(s => parseTime(s.duration) <= 1);
    else if (timeFilter === "1to2") result = result.filter(s => { const t = parseTime(s.duration); return t >= 1 && t <= 2; });
    else if (timeFilter === "3to4") result = result.filter(s => { const t = parseTime(s.duration); return t >= 3 && t <= 4; });
    else if (timeFilter === "over4") result = result.filter(s => parseTime(s.duration) > 4);

    return result;
  };

  return (
    <section
      id="services"
      className="py-24 md:py-32 bg-brand-surface relative noise-overlay"
      data-testid="services-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-sans text-brand-gold mb-4">
            What We Offer
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-text tracking-tight">
            Our Services
          </h2>
          <div className="gold-line-long mx-auto mt-6" />
        </motion.div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex justify-center mb-10">
            <TabsList className="bg-brand-bg border border-brand-border h-auto p-1 gap-1 flex-wrap" data-testid="service-tabs">
              {serviceCategories.map((cat) => (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  data-testid={`service-tab-${cat.value}`}
                  className="font-sans text-sm px-4 py-2 data-[state=active]:bg-brand-gold data-[state=active]:text-black data-[state=active]:shadow-none text-brand-text-muted rounded-none"
                >
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Smart Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-8" data-testid="service-filters">
            <div className="flex items-center gap-2">
              <DollarSign size={14} className="text-brand-gold" />
              <select
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
                data-testid="service-price-filter"
                className="bg-brand-bg border border-brand-border text-brand-text font-sans text-xs px-3 py-1.5 focus:outline-none focus:border-brand-gold cursor-pointer"
              >
                <option value="all">All Prices</option>
                <option value="under100">Under $100</option>
                <option value="100to200">$100 - $200</option>
                <option value="over200">Over $200</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-brand-gold" />
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                data-testid="service-time-filter"
                className="bg-brand-bg border border-brand-border text-brand-text font-sans text-xs px-3 py-1.5 focus:outline-none focus:border-brand-gold cursor-pointer"
              >
                <option value="all">All Times</option>
                <option value="under1">Under 1 hour</option>
                <option value="1to2">1-2 hours</option>
                <option value="3to4">3-4 hours</option>
                <option value="over4">4+ hours</option>
              </select>
            </div>
            {(priceFilter !== "all" || timeFilter !== "all") && (
              <button
                onClick={() => { setPriceFilter("all"); setTimeFilter("all"); }}
                className="font-sans text-xs text-brand-gold underline hover:text-brand-gold-light"
                data-testid="service-clear-filters"
              >
                Clear filters
              </button>
            )}
          </div>

          {serviceCategories.map((cat) => (
            <TabsContent key={cat.value} value={cat.value}>
              {(() => {
                const baseList = cat.value === "other"
                  ? services.filter((s) => s.category === "other" || s.category === "cornrows")
                  : services.filter((s) => s.category === cat.value);
                const filtered = applyFilters(baseList);
                return filtered.length === 0 ? (
                  <p className="text-center font-sans text-sm text-brand-text-muted py-12">No services match your filters. <button onClick={() => { setPriceFilter("all"); setTimeFilter("all"); }} className="text-brand-gold underline">Clear filters</button></p>
                ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {filtered.map((service, i) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    data-testid={`service-card-${service.id}`}
                    className="bg-brand-bg border border-brand-border group hover:-translate-y-1 transition-transform duration-300 overflow-hidden"
                  >
                    {service.image_url ? (
                      <div className="h-96 overflow-hidden">
                        <img
                          src={resolveUrl(service.image_url)}
                          alt={service.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    ) : (
                      <div className="h-1 bg-gradient-to-r from-brand-gold/60 via-brand-gold to-brand-gold/60" />
                    )}
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <h3 className="font-serif text-xl text-brand-text">
                          {service.name}
                        </h3>
                        <span className="font-sans font-bold text-lg text-brand-gold whitespace-nowrap">${service.starting_price}</span>
                      </div>
                      <button
                        onClick={() => { trackEvent("booking_click", { event_label: service.name, item_id: service.id }); window.location.href = `/?service=${service.id}#booking`; }}
                        data-testid={`book-${service.id}`}
                        className="w-full py-2.5 border border-brand-gold text-brand-gold font-sans text-sm font-medium hover:bg-brand-gold hover:text-black transition-colors duration-200"
                      >
                        Book This Service
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
                );
              })()}
            </TabsContent>
          ))}
        </Tabs>

        {/* Complete Price Menu */}
        <div className="mt-20" data-testid="full-price-list">
          <p className="text-xs uppercase tracking-[0.3em] font-sans text-brand-gold text-center mb-3">Price List</p>
          <h3 className="font-serif text-2xl md:text-3xl text-brand-text text-center mb-2">Complete Price Menu</h3>
          <p className="font-sans text-sm text-brand-text-muted text-center mb-10">Every style, every length — tap any row to book</p>
          {[["braids", "Braids"], ["twists", "Twists"], ["crochet", "Crochet & Ponytails"], ["locs", "Locs"], ["cornrows", "Cornrows"], ["mens", "Men's"], ["other", "Other"]].map(([catKey, catLabel]) => {
            const rows = services.filter((s) => s.category === catKey);
            if (rows.length === 0) return null;
            return (
              <div key={`pricegroup-${catKey}`} className="max-w-5xl mx-auto mb-10">
                <h4 className="font-serif text-lg text-brand-gold mb-3 border-b border-brand-gold/30 pb-2">{catLabel}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
                  {rows.map((s) => (
                    <button
                      key={`price-${s.id}`}
                      onClick={() => { trackEvent("booking_click", { event_label: s.name, item_id: s.id }); window.location.href = `/?service=${s.id}#booking`; }}
                      data-testid={`price-row-${s.id}`}
                      className="flex items-center gap-3 py-2 border-b border-brand-border/40 hover:border-brand-gold/60 text-left group transition-colors"
                    >
                      {s.image_url ? (
                        <img src={resolveUrl(s.image_url)} alt={s.name} loading="lazy" className="w-14 h-14 object-cover border border-brand-border flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 bg-brand-surface border border-brand-border flex-shrink-0 flex items-center justify-center">
                          <span className="text-brand-gold text-[10px] font-serif">AM</span>
                        </div>
                      )}
                      <span className="font-sans text-sm text-brand-text flex-1 group-hover:text-brand-gold transition-colors">{s.name}</span>
                      <span className="font-sans text-base text-brand-gold font-bold whitespace-nowrap">${s.starting_price}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Hair & Beauty Services (separate from braiding menu) */}
        <BeautyServicesSection services={services} />

        {/* Custom Style Request */}
        <CustomStyleForm />
      </div>
    </section>
  );
};

const CustomStyleForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", style_description: "", proposed_budget: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.style_description) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/custom-style-request`, form);
      toast.success("Request sent! We'll get back to you soon.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-16 border border-brand-gold/20 bg-brand-gold/5 p-8 text-center"
        data-testid="custom-style-success"
      >
        <CheckCircle size={32} className="text-brand-gold mx-auto mb-4" />
        <h3 className="font-serif text-xl text-brand-text mb-2">Request Received!</h3>
        <p className="font-sans text-sm text-brand-text-muted">We'll review your custom style request and get back to you within 24 hours with pricing.</p>
        <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", style_description: "", proposed_budget: "" }); }} className="mt-4 font-sans text-xs text-brand-gold hover:text-brand-gold-light transition-colors underline" data-testid="custom-style-reset">Submit another request</button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="mt-16 border border-brand-border bg-brand-bg p-6 md:p-8"
      data-testid="custom-style-form"
    >
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-sans text-brand-gold mb-2">Don't See Your Style?</p>
        <h3 className="font-serif text-2xl text-brand-text">Request a Custom Style</h3>
        <p className="font-sans text-sm text-brand-text-muted mt-2">Describe the style you want and your budget. We'll get back to you with a price!</p>
      </div>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" data-testid="custom-name-input" className="bg-brand-surface border-brand-border text-brand-text placeholder:text-brand-text-muted/50 rounded-none" />
        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" data-testid="custom-email-input" className="bg-brand-surface border-brand-border text-brand-text placeholder:text-brand-text-muted/50 rounded-none" />
        <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number *" data-testid="custom-phone-input" className="bg-brand-surface border-brand-border text-brand-text placeholder:text-brand-text-muted/50 rounded-none" />
        <Input value={form.proposed_budget} onChange={(e) => setForm({ ...form, proposed_budget: e.target.value })} placeholder="Your budget (e.g. $150-200)" data-testid="custom-budget-input" className="bg-brand-surface border-brand-border text-brand-text placeholder:text-brand-text-muted/50 rounded-none" />
        <div className="md:col-span-2">
          <Textarea value={form.style_description} onChange={(e) => setForm({ ...form, style_description: e.target.value })} placeholder="Describe the style you want in detail... (e.g. 'Goddess locs, waist length, with curly ends') *" data-testid="custom-style-input" className="bg-brand-surface border-brand-border text-brand-text placeholder:text-brand-text-muted/50 rounded-none min-h-[100px]" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={loading} data-testid="custom-style-submit" className="w-full py-3 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            <Send size={14} />
            {loading ? "Sending..." : "Send Style Request"}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default ServicesSection;
